import React, { FC, useCallback } from "react";
import DataTable, { SelectableRowHeader } from "Shared/components/tables/data-table";
import { PickOff } from "Shared/util/types";
import {
    PayoutInformationAccount,
    SavePayoutInformationAccountRequest,
} from "Shared/util/types/api";
import { savePayoutInformationAccount } from "Shared/util/web-api";
import { usePayoutInformationContext } from "../../context";
import { useDataTableColumns } from "../hooks";
import { DataTableItem } from "../types";
import {
    getRowId,
    isRowSelected,
    isSelectableRowDisabled,
    isSelectedAccount,
} from "../utils";
import { useTranslation } from "Shared/util/custom-hooks";

export const PayoutAccountDataTable: FC = () => {
    const {
        setPayoutAccountsState,
        setPayoutInformationState,
        values: { accounts },
    } = usePayoutInformationContext();

    const t = useTranslation(
        (x) => x.company_settings.payoutInformationPage.payoutAccountDataTable,
    );

    const saveAccount = useCallback(
        (request: SavePayoutInformationAccountRequest) => {
            savePayoutInformationAccount(request).then((result) => {
                setPayoutInformationState(result);
            });
        },
        [accounts, setPayoutAccountsState],
    );

    const handlePrimaryAccountChanged = useCallback(
        ({
            customerPaymentMethodId,
            openBankingAccountId,
            supportedCurrencies,
        }: PickOff<DataTableItem, "isPayoutAccount" | "isPrimary">) => {
            setPayoutAccountsState((prev) =>
                prev.map((prevAccount): PayoutInformationAccount => {
                    const isSelectedItem = isSelectedAccount(
                        openBankingAccountId,
                        customerPaymentMethodId,
                        prevAccount,
                    );

                    return isSelectedItem
                        ? {
                              ...prevAccount,
                              isPayoutAccount: true,
                              isPrimary: true,
                          }
                        : {
                              ...prevAccount,
                              isPrimary: false,
                          };
                }),
            );

            saveAccount({
                customerPaymentMethodId,
                isPayoutAccount: true,
                isPrimary: true,
                openBankingAccountId,
                supportedCurrencies,
            });
        },
        [saveAccount, setPayoutAccountsState],
    );

    const handleSupportedCurrenciesChanged = useCallback(
        (
            supportedCurrencies: DataTableItem["supportedCurrencies"],
            {
                customerPaymentMethodId,
                isPayoutAccount,
                isPrimary,
                openBankingAccountId,
            }: PickOff<DataTableItem, "supportedCurrencies">,
        ) => {
            setPayoutAccountsState((prev) =>
                prev.map((prevAccount): PayoutInformationAccount => {
                    const isSelectedItem = isSelectedAccount(
                        openBankingAccountId,
                        customerPaymentMethodId,
                        prevAccount,
                    );

                    return isSelectedItem
                        ? {
                              ...prevAccount,
                              supportedCurrencies: supportedCurrencies ?? [],
                          }
                        : prevAccount;
                }),
            );

            saveAccount({
                customerPaymentMethodId,
                isPayoutAccount,
                isPrimary,
                openBankingAccountId,
                supportedCurrencies,
            });
        },
        [saveAccount, setPayoutAccountsState],
    );

    const handlePayoutAccountChanged = (
        isPayoutAccount: DataTableItem["isPayoutAccount"],
        {
            customerPaymentMethodId,
            isPrimary,
            openBankingAccountId,
            supportedCurrencies,
        }: PickOff<DataTableItem, "isPayoutAccount">,
    ) => {
        saveAccount({
            customerPaymentMethodId,
            isPayoutAccount,
            isPrimary,
            openBankingAccountId,
            supportedCurrencies,
        });
    };

    const handleRowDeselected = (item: DataTableItem) => {
        handlePayoutAccountChanged(false, item);
    };

    const handleRowSelected = (item: DataTableItem) => {
        handlePayoutAccountChanged(true, item);
    };

    const columns = useDataTableColumns(
        handlePrimaryAccountChanged,
        handleSupportedCurrenciesChanged,
    );

    const selectableRowHeader:SelectableRowHeader = {
        hideSelectAll: true,
        title: t.active
    }

    return (
        <DataTable
            columns={columns}
            getRowId={getRowId}
            isSelected={isRowSelected}
            onRowDeselect={handleRowDeselected}
            onRowSelect={handleRowSelected}
            preSelectedRows={isRowSelected}
            rows={accounts}
            selectableRowDisabled={isSelectableRowDisabled}
            selectableRowHeader={selectableRowHeader}
            useSelectableRows
        />
    );
};
